/**
 * WorkflowPickerField - Picker for the `Workflow` action's `host.workflow.id`.
 *
 * Lists workflows in the same Logic App that have a single Request/Http
 * trigger (i.e., are callable). Excludes the current workflow.
 *
 * Mirrors the pattern used by Azure/LogicAppsUX
 * (apps/Standalone/.../Services/ChildWorkflow.ts ::
 * `getWorkflowsWithRequestTrigger`) but is rendered as a strict picker —
 * no custom value or expression mode.
 */

import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
  Combobox,
  Option,
  Spinner,
  type OptionOnSelectData,
  type SelectionEvents,
} from '@coreai/fluentui-react';
import { useCallableWorkflows, getCallableWorkflowInvocationIssue } from '@/hooks/useCallableWorkflows';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  combobox: {
    width: '100%',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  empty: {
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface WorkflowPickerFieldProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Name of the workflow the user is currently editing (excluded from list). */
  currentWorkflowName: string | undefined;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
}

export const WorkflowPickerField = ({
  name,
  label,
  value,
  onChange,
  currentWorkflowName,
  description,
  placeholder,
  required,
  error,
  disabled,
}: WorkflowPickerFieldProps) => {
  const styles = useStyles();
  const { data: workflows, isLoading, isError } = useCallableWorkflows(currentWorkflowName);

  const handleSelect = useCallback(
    (_event: SelectionEvents, data: OptionOnSelectData) => {
      onChange(data.optionValue ?? '');
    },
    [onChange],
  );

  const selectedOptions = useMemo(() => (value ? [value] : []), [value]);

  // When the picked workflow is known, surface any design-time invocation
  // issue (e.g., `relativePath` on a non-POST trigger — runtime would
  // otherwise reject the run with a BadRequest after the user clicks Run).
  // The explicit `error` prop from validation always wins over the picker's
  // own warning so we don't shadow upstream errors.
  const pickedWorkflow = useMemo(
    () => workflows.find((w) => w.name === value),
    [workflows, value],
  );
  const invocationIssue = useMemo(
    () => (pickedWorkflow ? getCallableWorkflowInvocationIssue(pickedWorkflow) : null),
    [pickedWorkflow],
  );
  const effectiveError = error ?? invocationIssue ?? undefined;

  // Empty-state messaging only applies when we *successfully* loaded zero
  // candidates. On error we surface the load-error instead so the user
  // doesn't see "no workflows" alongside "failed to load". And we never
  // disable the combobox while a value is set — the user still needs a
  // way to clear or replace a stored value that no longer matches any
  // option (e.g. legacy free-text from before the picker existed).
  const showEmptyState = !isLoading && !isError && workflows.length === 0;
  const shouldDisable = disabled || (showEmptyState && !value);
  const effectivePlaceholder =
    placeholder ??
    (showEmptyState
      ? 'No callable workflows in this app'
      : 'Select a workflow');

  return (
    <div className={styles.container}>
      <Label className={styles.label} htmlFor={name}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </Label>
      <Combobox
        id={name}
        autoComplete="off"
        className={styles.combobox}
        value={value}
        selectedOptions={selectedOptions}
        onOptionSelect={handleSelect}
        placeholder={effectivePlaceholder}
        disabled={shouldDisable}
        aria-describedby={description ? `${name}-description` : undefined}
        aria-invalid={!!effectiveError || undefined}
        // Strictly a picker — no freeform input.
        freeform={false}
      >
        {isLoading ? (
          <div className={styles.status} role="status" aria-live="polite">
            <Spinner size="tiny" aria-label={`Loading ${label}`} />
            <span>Loading workflows…</span>
          </div>
        ) : (
          workflows.map((w) => (
            <Option key={w.name} value={w.name} text={w.name}>
              {w.name}
            </Option>
          ))
        )}
      </Combobox>
      {showEmptyState && (
        <Text className={styles.empty}>
          No other workflows in this app have a callable Request trigger.
        </Text>
      )}
      {description && (
        <Text id={`${name}-description`} className={styles.description}>
          {description}
        </Text>
      )}
      {isError && !effectiveError && (
        <Text className={styles.error}>Failed to load workflows.</Text>
      )}
      {effectiveError && <Text className={styles.error}>{effectiveError}</Text>}
    </div>
  );
};
